import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/lib/api";

interface Settings {
  printing_api_url: string | null;
  shop_name: string;
  home_subtitle: string;
  home_title: string;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const [printingApiUrl, setPrintingApiUrl] = useState("");
  const [shopName, setShopName] = useState("");
  const [homeSubtitle, setHomeSubtitle] = useState("");
  const [homeTitle, setHomeTitle] = useState("");
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery<Settings>({
    queryKey: ["admin-settings"],
    queryFn: () => apiFetch<Settings>("/api/admin/settings"),
  });

  useEffect(() => {
    if (data) {
      setPrintingApiUrl(data.printing_api_url ?? "");
      setShopName(data.shop_name ?? "");
      setHomeSubtitle(data.home_subtitle ?? "");
      setHomeTitle(data.home_title ?? "");
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: {
      printing_api_url: string;
      shop_name: string;
      home_subtitle: string;
      home_title: string;
    }) =>
      apiFetch<Settings>("/api/admin/settings", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  function handleSave() {
    saveMutation.mutate({
      printing_api_url: printingApiUrl.trim(),
      shop_name: shopName.trim(),
      home_subtitle: homeSubtitle.trim(),
      home_title: homeTitle.trim(),
    });
  }

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Učitavanje...</p>;
  }

  return (
    <div className="flex flex-col gap-6 max-w-xl">
      <h1 className="text-xl sm:text-2xl font-bold">Postavke</h1>

      <div className="rounded-lg border border-border/60 bg-white p-4 sm:p-6 flex flex-col gap-4">
        <h2 className="text-sm font-semibold">Trgovina</h2>

        <div className="flex flex-col gap-2">
          <Label htmlFor="shop-name">Naziv trgovine</Label>
          <Input
            id="shop-name"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="home-subtitle">Podnaslov (početna stranica)</Label>
          <Input
            id="home-subtitle"
            value={homeSubtitle}
            onChange={(e) => setHomeSubtitle(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="home-title">Naslov početne stranice</Label>
          <Input
            id="home-title"
            value={homeTitle}
            onChange={(e) => setHomeTitle(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-white p-4 sm:p-6 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="printing-api-url">URL za štampu naljepnica</Label>
          <Input
            id="printing-api-url"
            type="url"
            placeholder="http://localhost:3000/api/stickers"
            value={printingApiUrl}
            onChange={(e) => setPrintingApiUrl(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            API prima POST zahtjev sa JSON telom: {"{"}"text": "SKU", "quantity": 5{"}"}
          </p>
        </div>

        {saveMutation.isError && (
          <p className="text-destructive text-sm">{saveMutation.error.message}</p>
        )}

        {saved && (
          <p className="text-sm text-green-600">Postavke sačuvane.</p>
        )}

        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="self-start"
        >
          {saveMutation.isPending ? "Spremanje..." : "Sačuvaj"}
        </Button>
      </div>
    </div>
  );
}
